import * as React from "react";

import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import Button from "@material-ui/core/Button";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { SyncBrief } from "../../../../../services/sync-license/types/sync-brief";
import { DetailContainer } from "../../controllers/releases/detail/common/detail-container";
import { createSyncBrief, getSyncBriefs } from "../../sdk/sync-license";
import styled from "styled-components";

const getPage = (
  next: string | undefined,
  currentBriefs: SyncBrief[],
  setPage: (page: SyncBrief[]) => void
) => {
  getSyncBriefs(next).then((data) => {
    const totalBriefs = [...currentBriefs, ...data.response].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    setPage(totalBriefs);
    if (data.nextToken) {
      getPage(data.nextToken, totalBriefs, setPage);
    }
  });
};

export const SyncBriefsPage: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const [subject, setSubject] = React.useState("");
  const [earningsPotential, setEarningsPotential] = React.useState("");
  const [submissionDeadline, setSubmissionDeadline] =
    React.useState<Date | null>(null);
  const [description, setDescription] = React.useState("");
  const [howtoapply, setHowtoapply] = React.useState("");

  const [syncBriefs, setSyncBriefs] = React.useState<SyncBrief[]>([]);

  React.useEffect(() => {
    setLoading(true);
    getPage(undefined, syncBriefs, setSyncBriefs);
    setLoading(false);
  }, [setLoading, setLoading]);

  const sendNewBrief = async () => {
    setLoading(true);
    if (
      subject &&
      earningsPotential &&
      submissionDeadline &&
      description &&
      howtoapply
    ) {
      await createSyncBrief({
        subject,
        description,
        submission_deadline: submissionDeadline,
        how_to_apply: howtoapply,
        earnings_potential: earningsPotential,
      });
      setSubject("");
      setEarningsPotential("");
      setSubmissionDeadline(null);
      setDescription("");
      setHowtoapply("");
      setLoading(true);
      getPage(undefined, syncBriefs, setSyncBriefs);
      setLoading(false);
    }
    setLoading(false);
  };

  return (
    <DetailContainer>
      <TitleWrapper>Sync Brief</TitleWrapper>
      <TextField
        label="Subject:"
        multiline
        fullWidth
        rows={3}
        value={subject}
        onChange={(ev) => setSubject(ev.target.value || "")}
        disabled={loading}
      />
      <TextField
        style={{ marginTop: "15px" }}
        label="Earnings Potential (Optional):"
        multiline
        fullWidth
        rows={3}
        value={earningsPotential}
        onChange={(ev) => setEarningsPotential(ev.target.value || "")}
        disabled={loading}
      />
      <DateWrapper>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <DatePicker
            label="Submission Deadline"
            variant="inline"
            fullWidth
            value={submissionDeadline}
            onChange={(date) => {
              setSubmissionDeadline(date);
            }}
          />
        </MuiPickersUtilsProvider>
      </DateWrapper>
      <TextField
        style={{ marginTop: "15px" }}
        label="Description"
        multiline
        fullWidth
        rows={4}
        value={description}
        onChange={(ev) => setDescription(ev.target.value || "")}
        disabled={loading}
      />
      <TextField
        style={{ marginTop: "15px", marginBottom: "10px" }}
        label="How to Apply"
        multiline
        fullWidth
        rows={3}
        value={howtoapply}
        onChange={(ev) => setHowtoapply(ev.target.value || "")}
        disabled={loading}
      />
      <Button
        disabled={
          loading ||
          !subject ||
          !submissionDeadline ||
          !description ||
          !howtoapply
        }
        onClick={() => sendNewBrief()}
        fullWidth
        variant="contained"
        style={{ marginBottom: "1rem" }}
      >
        {loading ? "Saving..." : "Save"}
      </Button>
      {syncBriefs.map((brief, i) => (
        <Accordion key={i} defaultExpanded={false}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            id={`panel-header-${i}`}
          >
            <Typography style={{ width: "150px" }}>
              {new Date(brief.created_at).toDateString()}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Flex>
              <Typography>
                <b>New Opportunity:</b>
                <br />
                {brief.subject}
              </Typography>
              <Typography>
                <b>Earnings Potential:</b>
                <br />
                {brief.earnings_potential}
              </Typography>
              <Typography>
                <b>Deadline to Apply:</b>
                <br />
                {new Date(brief.submission_deadline).toDateString()}
              </Typography>
              <Typography>
                <b>Details:</b>
                <br />
                {brief.description}
              </Typography>
              <Typography>
                <b>How to Apply:</b>
                <br />
                {brief.how_to_apply}
              </Typography>
            </Flex>
          </AccordionDetails>
        </Accordion>
      ))}
    </DetailContainer>
  );
};

const TitleWrapper = styled.h1`
  margin-top: 10px;
  margin-bottom: 10px;
`;

const Flex = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
`;

const DateWrapper = styled.div`
  max-width: 20rem !important;
  margin-top: 15px;
`;
